import React, { useState } from "react";
import PartnerList from "./PartnerList";
import CreatePartner from "./CreatePartner";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "../../../components/AppToast";
import { toPartnerApiStatus } from "../../../utils/partnerStatus";
import { setLoadTable } from "../../../redux/action/skuTable.action";
import { PARTNER_ENTITY } from "./partnerEntityConfig";
import { payloadToDummyRecord } from "./partnerDummyData";
import PartnerShipTab from "./PartnerShipTab";
import { shipModalTitle } from "./ShipAddressModal";
import { buildDisablePayload, loggedInPartnerId, loggedInTenantId } from "./storerForm";

const DETAILS_TAB = "details";
const SHIP_TAB = "ship";

/**
 * Shared management screen for Partner / Customer / Supplier.
 * Pass an `entity` descriptor from `partnerEntityConfig` to reuse this exact
 * list + form + API flow for another entity (defaults to Partner).
 */
const PartnerManagement = ({ entity = PARTNER_ENTITY }) => {
  const dispatch = useDispatch();
  const userDetail = useSelector((state) => state?.login?.userDetail);
  const { _id, tenantId } = userDetail || {};
  const [listVersion, setListVersion] = useState(0);

  const { label, screenTitle, api } = entity;
  const lowerLabel = label.toLowerCase();
  const useDummy = !!entity.useDummy;
  const [localRows, setLocalRows] = useState(() =>
    useDummy ? entity.dummyList.map((row) => ({ ...row })) : []
  );

  // UI modes
  const [isCreating, setIsCreating] = useState(false);
  const [activePartner, setActivePartner] = useState(null);
  const [editingPartnerId, setEditingPartnerId] = useState(null);

  /** Edit / View for Customer & Supplier are tabbed: "<Entity>" then "Ship To" / "Ship From". */
  const shipTabTitle = entity.ship?.title || shipModalTitle(label);
  const hasShipTab = !!entity.ship;
  const [activeFormTab, setActiveFormTab] = useState(DETAILS_TAB);
  // Mount the ship tab lazily (DataGrid needs real width) and keep it mounted afterwards.
  const [shipTabMounted, setShipTabMounted] = useState(false);

  const openFormTabs = () => {
    setActiveFormTab(DETAILS_TAB);
    setShipTabMounted(false);
  };

  const switchFormTab = (tab) => {
    if (tab === SHIP_TAB) setShipTabMounted(true);
    setActiveFormTab(tab);
  };


  // Create
  const handleCreateClick = () => {
    setIsCreating(true);
    setActivePartner(null);
    dispatch(setLoadTable({}));
  };

  // View
  const handleViewPartner = (rowData) => {
    if (!rowData) {
      toast.error(`Invalid ${lowerLabel} data`);
      return;
    }

    setActivePartner({
      mode: "view",
      initialValues: rowData,
    });
    setIsCreating(false);
    openFormTabs();
  };

  // Edit. `openShip` lands on the Ship To / Ship From tab.
  const handleEditPartner = (rowData, { openShip = false } = {}) => {
    if (!rowData) {
      toast.error(`Invalid ${lowerLabel} data`);
      return;
    }


    setEditingPartnerId(rowData._id);

    setActivePartner({
      mode: "edit",
      initialValues: rowData,
    });
    setIsCreating(false);
    if (openShip && hasShipTab) {
      setShipTabMounted(true);
      setActiveFormTab(SHIP_TAB);
    } else {
      openFormTabs();
    }
  };

  const handleOpenShip = (rowData) => handleEditPartner(rowData, { openShip: true });

  const handleDisablePartner = async (rowData) => {
    if (!rowData?._id || typeof api.disable !== "function") {
      toast.error(`No ${lowerLabel} selected for disable`);
      return;
    }
    const partnerId = loggedInPartnerId(userDetail);
    const tenantId = loggedInTenantId(userDetail);
    if (!partnerId || !tenantId) {
      toast.error("Partner and tenant are required to disable");
      return;
    }
    try {
      await api.disable(
        buildDisablePayload(rowData, entity.fieldPrefix, { partnerId, tenantId })
      );
      toast.success(`${label} disabled successfully!`);
      setListVersion((v) => v + 1);
    } catch (err) {
      toast.error(err?.message || `Failed to disable ${lowerLabel}`);
    }
  };

  // Create Submit
  const handleSubmit = async (data) => {
    const partnerId = loggedInPartnerId(userDetail);
    const payload = entity.storerApi
      ? { ...data, ...(partnerId ? { partnerId } : {}) }
      : {
          ...data,
          tenantId: tenantId?._id,
          users: _id,
        };

    if (useDummy) {
      // Customer / Supplier APIs are commented — keep the row in the local dummy list.
      setLocalRows((prev) => [payloadToDummyRecord(payload, `local-${Date.now()}`), ...prev]);
      toast.success(`${label} created successfully!`);
      closeForm();
      return;
    }

    try {
      await api.create(payload);
      toast.success(`${label} created successfully!`);
      closeForm();
      setListVersion((v) => v + 1);
    } catch (err) {
      toast.error(err?.message || `Failed to create ${lowerLabel}`);
    }
  };

  // Update Submit
  const handleUpdateSubmit = async (data) => {
    console.log(data, "updatePendingApprovalData");

    if (!editingPartnerId) {
      toast.error(`No ${lowerLabel} selected for update`);
      return;
    }
    if (useDummy) {
      // Customer / Supplier APIs are commented — update the local dummy row only.
      setLocalRows((prev) =>
        prev.map((row) =>
          row._id === editingPartnerId
            ? { ...payloadToDummyRecord(data, editingPartnerId), createdAt: row.createdAt }
            : row
        )
      );
      toast.success(`${label} updated successfully!`);
      closeForm();
      setEditingPartnerId(null);
      return;
    }

    try {
      if (entity.storerApi) {
        await api.update(data, editingPartnerId);
      } else {
        const statusNum = toPartnerApiStatus(data.status, 1);

        if (statusNum === 2) {
          await api.activate({ ...data, status: "Active" }, editingPartnerId);
        } else {
          await api.update({ ...data, status: statusNum }, editingPartnerId);
        }
      }

      toast.success(`${label} updated successfully!`);
      closeForm();
      setEditingPartnerId(null);
      setListVersion((v) => v + 1);
    } catch (err) {
      toast.error(err?.message || `Failed to update ${lowerLabel}`);
    }
  };

  // Close form
  const closeForm = () => {
    setActivePartner(null);
    setIsCreating(false);
    setEditingPartnerId(null);
    openFormTabs();
    dispatch(setLoadTable({}));
    setListVersion((v) => v + 1);
  };

  const onlyFormVisible = isCreating || !!activePartner;

  return (
    <div className="card shadow-lg main-card" style={{ position: "relative" }}>
      {/* Header */}
      <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
        <h4 className="mb-0 h6 fw-semibold">
          {activePartner
            ? activePartner.mode === "view"
              ? `View ${label}`
              : `Edit ${label}`
            : isCreating
              ? `Add New ${label}`
              : `Manage ${label}`}
        </h4>
      </div>

      {/* Breadcrumb */}
      {(isCreating || activePartner) && (
        <nav
          aria-label="breadcrumb"
          className="px-3 py-2 border-bottom bg-light mb-2"
        >
          <ol className="breadcrumb mb-0 align-items-center">
            <li className="breadcrumb-item">
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-muted"
                onClick={closeForm}
              >
                {screenTitle}
              </button>
            </li>
            <li
              className="breadcrumb-item active fw-semibold text-primary"
              aria-current="page"
            >
              {isCreating
                ? `Create ${label}`
                : activePartner?.mode === "edit"
                  ? `Edit ${label}`
                  : `View ${label}`}
            </li>
          </ol>
        </nav>
      )}

      {/* Body */}
      <div className="card-body">
        {/* List */}
        {!onlyFormVisible && (
          <PartnerList
            onViewPartner={handleViewPartner}
            onEditPartner={handleEditPartner}
            refreshKey={listVersion}
            showCreateButton={true}
            createButtonText={`Add New ${label}`}
            createButtonIcon="bi-plus-circle"
            onCreateClick={handleCreateClick}
            entityLabel={label}
            fetchList={api.list}
            tableId={entity.tableId}
            exportImportPath={entity.exportImportPath}
            sampleFileUrl={entity.sampleFileUrl}
            sampleFileName={entity.sampleFileName}
            localRows={useDummy ? localRows : null}
            fallbackRows={entity.storerApi ? null : entity.fallbackList || null}
            fieldPrefix={entity.fieldPrefix}
            visibleFields={entity.listFields}
            onDisable={typeof api.disable === "function" ? handleDisablePartner : undefined}
            onOpenShip={hasShipTab ? handleOpenShip : undefined}
            shipActionLabel={shipTabTitle}
          />
        )}

        {/* Create Form */}
        {isCreating && !activePartner && (
          <CreatePartner
            onSubmit={handleSubmit}
            onCancel={closeForm}
            reset={closeForm}
            submitText={`Create ${label}`}
            cancelText="Back"
            readOnly={false}
            entityLabel={label}
            hiddenFieldNames={entity.hiddenFieldNames}
            hideSections={entity.hideSections}
            hideDocuments={entity.hideDocuments}
            optionalFieldNames={entity.optionalFieldNames}
            addressRequiredFields={entity.addressRequiredFields}
            hideOperationalHours={entity.hideOperationalHours}
            hideTerms={entity.hideTerms}
            formVariant={entity.formVariant}
            fieldPrefix={entity.fieldPrefix}
          />
        )}

        {/* Edit / View Form (tabbed for Customer / Supplier) */}
        {activePartner && (
          <>
            {hasShipTab && (
              <ul className="nav nav-tabs pp-entity-tabs mb-3" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeFormTab === DETAILS_TAB}
                    className={`nav-link ${activeFormTab === DETAILS_TAB ? "active" : ""}`}
                    onClick={() => switchFormTab(DETAILS_TAB)}
                  >
                    <i className={`bi ${label === "Supplier" ? "bi-shop" : "bi-person-vcard"} me-1`} aria-hidden />
                    {label}
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeFormTab === SHIP_TAB}
                    className={`nav-link ${activeFormTab === SHIP_TAB ? "active" : ""}`}
                    onClick={() => switchFormTab(SHIP_TAB)}
                  >
                    <i className="bi bi-geo-alt me-1" aria-hidden />
                    {shipTabTitle}
                  </button>
                </li>
              </ul>
            )}

            {/* Keep the form mounted while on the ship tab so unsaved edits survive tab switches. */}
            <div style={{ display: activeFormTab === DETAILS_TAB ? "block" : "none" }}>
              <CreatePartner
                initialValues={activePartner.initialValues}
                onSubmit={handleUpdateSubmit}
                onCancel={closeForm}
                submitText={
                  activePartner.mode === "edit" ? `Update ${label}` : "Close"
                }
                cancelText="Back"
                readOnly={activePartner.mode === "view"}
                isEdit={activePartner?.mode === "edit"}
                entityLabel={label}
                hiddenFieldNames={entity.hiddenFieldNames}
                hideSections={entity.hideSections}
                hideDocuments={entity.hideDocuments}
                optionalFieldNames={entity.optionalFieldNames}
                addressRequiredFields={entity.addressRequiredFields}
                hideOperationalHours={entity.hideOperationalHours}
                hideTerms={entity.hideTerms}
                formVariant={entity.formVariant}
                fieldPrefix={entity.fieldPrefix}
              />
            </div>

            {hasShipTab && shipTabMounted && (
              <div style={{ display: activeFormTab === SHIP_TAB ? "block" : "none" }}>
                <PartnerShipTab
                  key={activePartner.initialValues?._id || "ship-tab"}
                  entityLabel={label}
                  partnerRow={activePartner.initialValues}
                  readOnly={activePartner.mode === "view"}
                  ship={entity.ship}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PartnerManagement;
